// src/pages/PrePaidDeliveryView.jsx
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box, Container, Paper, Typography, CircularProgress, Alert,
  Divider, Button, Card, Snackbar, Chip,
} from '@mui/material';
import {
  MapPin, Package, ArrowRight, Clock, Calendar,
  Bus, AlertCircle, CheckCircle2, User, Printer, Mail,
} from 'lucide-react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../context/AuthContext';
import { BusTransportModal } from '../shared/components/Modals/BusTransportModal/BusTransportModal';
import { createDeliveryFn } from '../firebase/deliveryFunctions';
import { printDeliveryWaybill } from '../utils/printUtils/index';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function toDateMaybe(value) {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'string') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  if (value instanceof Date) return value;
  return null;
}

function formatDateTime(date) {
  if (!date) return '—';
  return date.toLocaleString('fi-FI', {
    weekday: 'short', day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit',
  });
}

function generateActivationCode() {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  return Array.from({ length: 5 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

// ─── Component ───────────────────────────────────────────────────────────────

export function PrePaidDeliveryView() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const refId = searchParams.get('r');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Bus transport
  const [busModalOpen, setBusModalOpen] = useState(false);
  const [selectedBusOption, setSelectedBusOption] = useState(null);
  const [busPickupTime, setBusPickupTime] = useState(null);
  const [busTargetTime, setBusTargetTime] = useState(null);

  // Activation code (eco-regular / posti)
  const [activationCode, setActivationCode] = useState(null);

  // Submission
  const [submittingBus, setSubmittingBus] = useState(false);
  const [submittingPosti, setSubmittingPosti] = useState(false);
  const [createdDeliveryId, setCreatedDeliveryId] = useState(null);
  const [createdDeliveryData, setCreatedDeliveryData] = useState(null);

  // Toast
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });
  const showToast = (message, severity = 'info') => setToast({ open: true, message, severity });

  // ─── Fetch ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!refId) { setError('Linkin tunniste puuttuu.'); setLoading(false); return; }

    const fetchData = async () => {
      try {
        setLoading(true);
        const snap = await getDoc(doc(db, 'initialValues', refId));
        if (!snap.exists()) {
          setError('Lähetystä ei löytynyt tai linkki on vanhentunut.');
          return;
        }
        const docData = snap.data();

        // Restore any previously completed state
        if (docData.busTransportData) setSelectedBusOption(docData.busTransportData);
        if (docData.busPickupTime)    setBusPickupTime(docData.busPickupTime);
        if (docData.busTargetTime)    setBusTargetTime(docData.busTargetTime);
        if (docData.activationCode)   setActivationCode(docData.activationCode);
        if (docData.createdDeliveryId) setCreatedDeliveryId(docData.createdDeliveryId);

        setData(docData);
      } catch (err) {
        console.error('Error fetching pre-paid delivery:', err);
        setError('Virhe ladattaessa lähetystietoja.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [refId]);

  // ─── Derived flags ────────────────────────────────────────────────────────

  const methods = data?.deliveryMethods ?? [];
  const hasBusTransport = methods.includes('eco-bus');
  const hasPostiTransport = methods.includes('eco-regular');

  // ─── Bus confirm ──────────────────────────────────────────────────────────

  const handleBusConfirm = (price, busOption, departureMs, arrivalMs) => {
    setSelectedBusOption({ ...busOption, price });
    setBusPickupTime(departureMs ?? null);
    setBusTargetTime(arrivalMs  ?? null);
    setBusModalOpen(false);
  };

  // ─── Create bus delivery ──────────────────────────────────────────────────

  const handleCreateBusDelivery = async () => {
    if (!user?.uid) {
      navigate('/auth', { state: { from: { pathname: `/tilaus?r=${refId}` } } });
      return;
    }
    if (!selectedBusOption) { showToast('Valitse ensin bussivuoro.', 'warning'); return; }

    try {
      setSubmittingBus(true);

      const price  = data.price || selectedBusOption?.price || 0;
      const reward = data.reward || Number((price * 0.7).toFixed(2));

      const emptyDetails      = { apartment: '', floor: '', building: '', accessCode: '', instructions: '' };
      const emptyLocationInfo = (addr) => ({
        address: addr || '', latitude: null, longitude: null,
        placeId: null, city: '', postalCode: '', details: emptyDetails,
      });

      const originInfo      = data.originInfo      ?? data.fromInfo ?? emptyLocationInfo(data.origin ?? data.from);
      const destinationInfo = data.destinationInfo ?? data.toInfo   ?? emptyLocationInfo(data.destination ?? data.to);
      const origin          = data.origin          ?? data.from     ?? '';
      const destination     = data.destination     ?? data.to       ?? '';

      const now = Date.now();
      const pickupTime = busPickupTime ?? toDateMaybe(data.pickupTime ?? data.pickUpTime)?.getTime() ?? now;
      const targetTime = busTargetTime ?? toDateMaybe(data.targetTime)?.getTime() ?? now + 3 * 3600000;

      const payload = {
        senderId: user.uid,
        origin,
        originInfo,
        destination,
        destinationInfo,
        senderPhone:   data.senderPhone   || null,
        senderInfo:    data.senderInfo    || { name: null, email: null },
        receiverPhone: data.receiverPhone || null,
        receiverInfo:  data.receiverInfo  || { name: null, email: null },
        pickupTime,
        targetTime,
        latestPickUpTime:   data.latestPickUpTime   ?? data.latestPickupTime ?? null,
        earliestTargetTime: data.earliestTargetTime ?? null,
        hasTimeWindow:      data.hasTimeWindow      ?? false,
        price,
        reward,
        deliveryType:          'busTransport',
        deliveryTransportType: 'eco-bus',
        distance:    data.distance    || null,
        duration:    data.duration    || null,
        packageSize: data.packageSize || 'small',
        weightKg:    data.weightKg    || 5,
        billing: {
          paymentMethod:     data.paymentMethod || 'prePaid',
          yNumber:           null,
          companyName:       null,
          paymentReference:  null,
          billingDepartment: null,
        },
        busTransportData: selectedBusOption,
        prePaidRef:       refId,
        prePaidCreatorId: data.creatorId || null,
        hide:             false,
      };

      const result = await createDeliveryFn(payload);

      await updateDoc(doc(db, 'initialValues', refId), {
        status:            'completed',
        createdDeliveryId: result.deliveryId,
        completedAtMs:     Date.now(),
        completedByUserId: user.uid,
        busTransportData:  selectedBusOption,
        busPickupTime:     busPickupTime ?? null,
        busTargetTime:     busTargetTime ?? null,
      });

      setCreatedDeliveryId(result.deliveryId);
      setCreatedDeliveryData({ ...payload, deliveryId: result.deliveryId });
      showToast('Bussitoimitus vahvistettu!', 'success');
    } catch (err) {
      console.error('Error creating bus delivery:', err);
      showToast('Virhe luotaessa lähetystä. Yritä uudelleen.', 'error');
    } finally {
      setSubmittingBus(false);
    }
  };

  // ─── Generate activation code (posti) ────────────────────────────────────

  const handleGenerateActivationCode = async () => {
    if (!user?.uid) {
      navigate('/auth', { state: { from: { pathname: `/tilaus?r=${refId}` } } });
      return;
    }

    try {
      setSubmittingPosti(true);
      const code = generateActivationCode();

      await updateDoc(doc(db, 'initialValues', refId), {
        activationCode:    code,
        activationCodeGeneratedAtMs: Date.now(),
        activationCodeGeneratedByUserId: user.uid,
      });

      setActivationCode(code);
      showToast('Aktivointikoodi luotu!', 'success');
    } catch (err) {
      console.error('Error generating activation code:', err);
      showToast('Virhe aktivointikoodin luomisessa.', 'error');
    } finally {
      setSubmittingPosti(false);
    }
  };

  // ─── Loading / Error ──────────────────────────────────────────────────────

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Ladataan...</Typography>
      </Container>
    );
  }

  if (error || !data) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Alert severity="error" icon={<AlertCircle size={20} />} sx={{ mb: 2 }}>
          {error || 'Lähetystä ei löytynyt.'}
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/')}>Palaa etusivulle</Button>
      </Container>
    );
  }

  // ─── Derived display values ───────────────────────────────────────────────

  const originInfo      = data.originInfo      ?? data.fromInfo ?? {};
  const destinationInfo = data.destinationInfo ?? data.toInfo   ?? {};
  const originAddress   = originInfo.address      ?? data.origin ?? data.from ?? '—';
  const destAddress     = destinationInfo.address ?? data.destination ?? data.to ?? '—';

  const pickupDate = toDateMaybe(busPickupTime ?? data.pickupTime ?? data.pickUpTime);
  const targetDate = toDateMaybe(busTargetTime ?? data.targetTime);

  // ─── Main render ──────────────────────────────────────────────────────────

  return (
    <Container maxWidth="sm" sx={{ py: 4, pt: 12 }}>
      <Paper elevation={3} sx={{ p: { xs: 3, sm: 4 }, borderRadius: 2 }}>

        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box sx={{ display: 'inline-flex', p: 2, bgcolor: 'primary.main', color: 'white', borderRadius: '50%', mb: 2 }}>
            <Package size={28} />
          </Box>
          <Typography variant="h6" fontWeight={700}>Lähetyksen tiedot</Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Route */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Nouto-osoite
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <MapPin size={16} color="#1976d2" />
              <Typography variant="body1" fontWeight={500}>{originAddress}</Typography>
            </Box>
            {originInfo.city && (
              <Typography variant="body2" color="text.secondary" sx={{ pl: 3 }}>
                {originInfo.postalCode} {originInfo.city}
              </Typography>
            )}
          </Box>

          <Box sx={{ pl: 1.5, py: 0.5 }}>
            <ArrowRight size={18} color="#9ca3af" />
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Toimitusosoite
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <MapPin size={16} color="#1976d2" />
              <Typography variant="body1" fontWeight={500}>{destAddress}</Typography>
            </Box>
            {destinationInfo.city && (
              <Typography variant="body2" color="text.secondary" sx={{ pl: 3 }}>
                {destinationInfo.postalCode} {destinationInfo.city}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Contacts */}
        {(data.senderInfo?.name || data.receiverInfo?.name) && (
          <>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 3 }}>
              {data.senderInfo?.name && (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Lähettäjä
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <User size={15} />
                    <Typography variant="body2" fontWeight={500}>{data.senderInfo.name}</Typography>
                  </Box>
                  {data.senderPhone && (
                    <Typography variant="body2" color="text.secondary">{data.senderPhone}</Typography>
                  )}
                </Box>
              )}
              {data.receiverInfo?.name && (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Vastaanottaja
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <User size={15} />
                    <Typography variant="body2" fontWeight={500}>{data.receiverInfo.name}</Typography>
                  </Box>
                  {data.receiverPhone && (
                    <Typography variant="body2" color="text.secondary">{data.receiverPhone}</Typography>
                  )}
                </Box>
              )}
            </Box>
          </>
        )}

        {/* Times */}
        {(pickupDate || targetDate) && (
          <>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 3 }}>
              {pickupDate && (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Noutoaika
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Calendar size={15} />
                    <Typography variant="body2" fontWeight={500}>{formatDateTime(pickupDate)}</Typography>
                  </Box>
                </Box>
              )}
              {targetDate && (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Toimitusaika
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Clock size={15} />
                    <Typography variant="body2" fontWeight={500}>{formatDateTime(targetDate)}</Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </>
        )}

        {/* Selected bus option */}
        {selectedBusOption && (
          <>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ mb: 3 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Valittu bussivuoro
              </Typography>
              <Card variant="outlined" sx={{ p: 2, borderRadius: 2, mt: 1, borderColor: 'success.main', bgcolor: 'success.50' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Bus size={16} />
                  <Typography variant="body2" fontWeight={600}>
                    {selectedBusOption.name || selectedBusOption.title || 'Bussipaketti'}
                  </Typography>
                </Box>
              </Card>
              {!createdDeliveryId && (
                <Button variant="text" size="small" onClick={() => setBusModalOpen(true)} sx={{ mt: 0.5 }}>
                  Vaihda bussivuoro
                </Button>
              )}
            </Box>
          </>
        )}

        {/* Activation code */}
        {activationCode && (
          <>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ mb: 3 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Postin aktivointikoodi
              </Typography>
              <Card variant="outlined" sx={{ p: 2, borderRadius: 2, mt: 1, borderColor: 'info.main', bgcolor: 'info.50', textAlign: 'center' }}>
                <Typography variant="h5" fontWeight={800} letterSpacing={4}>
                  {activationCode}
                </Typography>
                <Button
                  size="small" variant="text" sx={{ mt: 0.5 }}
                  onClick={() => {
                    navigator.clipboard.writeText(activationCode);
                    showToast('Koodi kopioitu!', 'success');
                  }}
                >
                  Kopioi koodi
                </Button>
              </Card>
            </Box>
          </>
        )}

        <Divider sx={{ mb: 3 }} />

        {/* CTAs */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>

          {/* Bus flow */}
          {hasBusTransport && !selectedBusOption && (
            <Button
              variant="contained" size="large" fullWidth
              onClick={() => setBusModalOpen(true)}
              startIcon={<Bus size={18} />}
            >
              Hae bussivuoro
            </Button>
          )}
          {hasBusTransport && selectedBusOption && !createdDeliveryId && (
            <Button
              variant="contained" size="large" fullWidth color="success"
              onClick={handleCreateBusDelivery} disabled={submittingBus}
              startIcon={submittingBus ? <CircularProgress size={18} color="inherit" /> : <CheckCircle2 size={18} />}
            >
              {submittingBus ? 'Luodaan...' : 'Vahvista bussitoimitus'}
            </Button>
          )}
          {hasBusTransport && createdDeliveryId && createdDeliveryData && (
            <Button
              variant="outlined" size="large" fullWidth
              onClick={() => printDeliveryWaybill(createdDeliveryData)}
              startIcon={<Printer size={18} />}
            >
              Lataa rahtikirja
            </Button>
          )}

          {/* Posti flow */}
          {hasPostiTransport && !activationCode && (
            <Button
              variant={hasBusTransport ? 'outlined' : 'contained'} size="large" fullWidth
              onClick={handleGenerateActivationCode} disabled={submittingPosti}
              startIcon={submittingPosti ? <CircularProgress size={18} color="inherit" /> : <Mail size={18} />}
            >
              {submittingPosti ? 'Luodaan...' : 'Luo aktivointikoodi'}
            </Button>
          )}
          {hasPostiTransport && activationCode && (
            <Chip
              icon={<CheckCircle2 size={15} />}
              label="Postikoodi aktivoitu"
              color="success"
              variant="outlined"
              sx={{ alignSelf: 'center' }}
            />
          )}

          {!user && (
            <Typography variant="caption" color="text.secondary" textAlign="center">
              Lähetyksen luominen vaatii kirjautumisen.
            </Typography>
          )}
        </Box>

      </Paper>

      {hasBusTransport && (
        <BusTransportModal
          open={busModalOpen}
          onClose={() => setBusModalOpen(false)}
          origin={originAddress}
          destination={destAddress}
          pickupTime={data.pickupTime ?? data.pickUpTime}
          onConfirm={handleBusConfirm}
        />
      )}

      <Snackbar
        open={toast.open} autoHideDuration={4000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.severity} variant="filled" sx={{ width: '100%', boxShadow: 3 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}